<template>
  <reduced-layout-fi
    class="login-form-page-fi"
    :brand="brand"
    :api-data="apiData"
    :logo-src="apiData?.Header_Logo?.Src"
    :logo-alt="apiData?.Header_Logo?.Alt"
    :interface-language="interfaceLanguage"
    @change-language="changeLanguage"
  >
    <loading-indicator
      v-if="!isMounted || apiDataLoading"
      variant="spinnerFullPageCover"
    />
    <template v-else>
      <div v-if="isLoginFailure || isLoginImpossible">
        <error-message
          ref="errorMessage"
          :title="apiData?.Login_Failed_Title || 'Login failed'"
          scroll-to="siteLoginStandalone"
          data-test="login-error"
        >
          {{ apiData?.Login_Failed_Error || '' }}
        </error-message>
      </div>
      <div
        v-if="!isLoginImpossible"
        id="siteLoginStandalone"
        class="login-form-page-fi__login"
      >
        <login-form-field-fi
          ref="loginForm"
          :password="form.password"
          :username="form.username"
        />
        <div
          class="login-form-page-fi__form"
          data-test="login-form"
        >
          <headline-fi
            class="login-form-page-fi__headline fi-text-size-df-30"
            headline-tag="h1"
          >
            {{ apiData.Headline }}
          </headline-fi>

          <text-input-fi
            id="email"
            ref="username"
            v-model.trim="form.username"
            class="login-form-page-fi__username"
            autocomplete="username"
            name="username"
            data-test="email-input"
            :rules="
              userIdentifierRule(brand === 'fite', form.username, enableUserIdentifierValidation)
            "
            :disabled="isLoginImpossible"
            :label="apiData.Email_Placeholder"
            @keyup="validateCharacters(form.username)"
            @blur="enableUserIdentifierValidation = true"
            @update:model-value="onChangeUsername"
            @validation-changed="
              (value) => {
                form.usernameValid = value;
              }
            "
            @keyup-enter="onEnterEmail"
          />
          <text-input-fi
            id="password"
            ref="password"
            v-model="form.password"
            class="login-form-page-fi__password"
            autocomplete="current-password"
            name="password"
            type="password"
            data-test="password-input"
            :rules="passwordChangedOrBlurred ? rules.required : []"
            :disabled="isLoginImpossible"
            :label="apiData.Password_Placeholder"
            @update:model-value="onChangePassword"
            @blur="passwordChangedOrBlurred = true"
            @validation-changed="
              (value) => {
                form.passwordValid = value;
              }
            "
            @keyup-enter="onEnterPassword"
          />

          <div v-if="apiData.Password_Reset">
            <link-fi
              :href="apiData.Password_Reset.Url"
              :target="apiData.Password_Reset.Target"
              data-test="req-new-cred"
              class="login-form-page-fi__password-reset fi-text-size-df-18"
            >
              {{ apiData.Password_Reset.Text }}
            </link-fi>
          </div>

          <button-fi
            type="submit"
            class="button-fi__primary button-fi--stretched button-fi--large"
            data-test="login-button"
            :loading="isLoading"
            :disabled="submitDisabled"
            @click="submit"
          >
            {{ apiData.Submit }}
          </button-fi>

          <div
            v-if="apiData.Registration"
            class="login-form-page-fi__registration fi-text-size-df-18"
          >
            {{ apiData.Registration_Headline }}
            <button-fi
              :target="apiData.Registration.Target"
              :href="apiData.Registration.Url"
              :loading="isLoading"
              :disabled="isLoginImpossible"
              class="button-fi__tertiary button-fi--stretched button-fi--large login-form-page-fi__registration-btn"
              data-test="registration-btn"
              natural-events
            >
              {{ apiData.Registration.Text }}
            </button-fi>
          </div>
        </div>
      </div>
    </template>
  </reduced-layout-fi>
</template>

<script>
import { ButtonFi, ErrorMessage, HeadlineFi, LinkFi, LoadingIndicator, TextInputFi } from 'atoms';
import axios from 'axios';
import LoginFormFieldFi from 'components/login-dialog-fi/LoginFormFieldFi.vue';
import loginMixin from 'components/my-account/site-login/loginMixin';
import ReducedLayoutFi from 'components/reduced-layout-fi/ReducedLayoutFi';
import { goToLoginFunction } from 'composables/login';
import validatorsMixin from 'mixins/validatorsMixin';

import Validators from '@/utils/validators';

const loginRedirectCookie = 'dialog-redirect';

const queryToObject = (queryString) => {
  const searchParams = new URLSearchParams(queryString);
  return Object.fromEntries(searchParams.entries());
};

export default {
  name: 'LoginFormPageFi',
  components: {
    ButtonFi,
    ErrorMessage,
    HeadlineFi,
    LinkFi,
    LoadingIndicator,
    LoginFormFieldFi,
    ReducedLayoutFi,
    TextInputFi,
  },
  mixins: [loginMixin, validatorsMixin],
  inject: ['fields'],
  data() {
    return {
      form: {
        password: '',
        passwordValid: false,
        usernameValid: false,
        username: '',
      },
      usernameChangedOrBlurred: false,
      passwordChangedOrBlurred: false,
      isLoading: false,
      isLoginImpossible: false,
      enableUserIdentifierValidation: false,
      isLoginFailure: false,

      brand: '',
      lang: '',

      interfaceLanguage: null,
      apiData: null,
      apiDataLoading: true,
      isSubmitting: false,
      isMounted: false,
    };
  },
  computed: {
    formValid() {
      return this.form.passwordValid && this.form.usernameValid;
    },
    rules() {
      return Validators(
        {
          get: (key) =>
            ({
              'online-Shop.Global-Labels.Error-Message-Required': this.apiData?.Mandatory_Error,
            })[key] || key,
        },
        {
          emailFieldValidationMsgInvalidFormat: {
            value: this.apiData?.Invalid_Email_Error,
          },
        },
      );
    },
    submitDisabled() {
      return (
        this.isSubmitting ||
        this.isLoginImpossible ||
        ((this.passwordChangedOrBlurred || this.usernameChangedOrBlurred) &&
          (!this.formValid || this.form.password === null))
      );
    },
    referrerData() {
      this.logDebug('checking referrer data', this.referrer);
      const json = this.fields?.referrerData?.value;
      if (json) {
        try {
          const data = JSON.parse(json);
          const customData = data[this.referrer];
          if (customData) {
            this.logDebug('got referrer data', customData);
            return customData;
          }
        } catch (error) {
          console.error(error, json);
        }
      } else {
        this.logDebug('no referrer data configured');
      }
      this.logDebug('no referrer data');
      return {};
    },
    referrer() {
      if (IS_SHOWROOM) {
        return this.$fischer.query('referrer') || 'showroom';
      }
      const [, host] = /^https?:\/\/([^/]+)(?:\/|$)/.exec(window.document.referrer) || [];
      return host;
    },
  },
  mounted() {
    this.isMounted = true;
  },
  async created() {
    try {
      if (this.redirectFromOldService()) {
        return;
      }
      const brand = this.$fischer.query('brand');
      if (brand && !brand.startsWith('dialog:')) {
        this.brand = brand;
      }

      if (IS_SSR) {
        return;
      }
      if (!(await this.handleLoginRequest())) {
        this.handleLoginFormPage();
      }
    } catch (error) {
      console.error('Error in login form page created hook', error);
    }
  },
  methods: {
    redirectFromOldService() {
      if (IS_SSR) {
        return false;
      }
      let newLocation = String(window.location);
      let oldServices = 'https://services.ugfischer.com/';
      let newServices = import.meta.env.VITE_OPENID_SERVICES;
      for (let level = 0; level < 3; level += 1) {
        const oldServiceRegEx = new RegExp(oldServices.replace(/\./g, '\\.'), 'g');
        newLocation = newLocation.replace(oldServiceRegEx, newServices);

        oldServices = encodeURIComponent(oldServices);
        newServices = encodeURIComponent(newServices);
      }
      if (String(window.location) !== newLocation) {
        console.debug('old services detected, redirecting to new services');
        window.location = newLocation;
        return true;
      }
      return false;
    },
    getCookieName() {
      return 'site-login';
    },
    async handleLoginRequest() {
      if (IS_SSR) {
        return true;
      }
      if (this.fields?.ssoPoc?.value) {
        this.loadApiData('de-DE', 'fite');
        this.logDebug('not handlingg login request - sso poc');
        return true;
      }
      this.logDebug('handling login request');

      const hash = queryToObject(window.location.hash.substr(1));
      const search = queryToObject(window.location.search.substr(1));
      const hint = {};
      if (search.login_hint) {
        // NOTE: this is not the standard way to use it
        ['api_key', 'lang', 'brand'].forEach((key) => {
          const match = new RegExp(`(?:^|\\.)${key}-([^.]+)`).exec(search.login_hint);
          if (match) {
            const [, value] = match;
            hint[key] = value;
          }
        });
      }
      const {
        lang,
        brand,
        state,
        nonce,
        client_id: clientIdParam,
        redirect_uri: redirectUri,
        api_key: apiKey,
        scope = 'openid',
        response_type: responseType = 'id_token token',
        ui_locales: uiLocales,
        password,
        username,
      } = {
        ...search,
        ...hint,
        ...hash,
      };

      const cookies = this.getCookies();

      if (lang) {
        cookies.lang = lang.replace('_', '-');
      }
      if (uiLocales) {
        const [locale] = uiLocales.split(' ');
        cookies.lang = locale.replace('_', '-');
      }
      if (brand) {
        cookies.brand = brand;
      }
      if (apiKey) {
        cookies.apiKey = apiKey;
      }

      let clientId = clientIdParam || cookies.clientId;
      if (!clientId) {
        const [langShort] = (cookies.lang || 'de').split(/[_-]/);
        clientId = `fiwe-${langShort.toLowerCase()}`;
      }
      if (clientId === 'fite-de') {
        clientId = 'fite-int';
      }
      cookies.clientId = clientId;
      this.clientId = clientId;

      const searchParams = new URLSearchParams(window.location.search);
      const authorizeRedirectParam = new URLSearchParams(searchParams.get('authorize_redirect'));
      const originalRedirect = authorizeRedirectParam.get('redirect_uri');
      if (brand?.startsWith('dialog:') && originalRedirect) {
        this.logDebug('redirecting back to custom login page');
        searchParams.delete('state');
        const brandPath = /^dialog:([^?#@]+)?/.exec(brand)?.[1];
        const authRedirectUrl = new URL(originalRedirect);
        const newLoginRedirect = `${authRedirectUrl.origin}${brandPath}?${searchParams}${window.location.hash}`;
        this.$cookies.set(
          loginRedirectCookie,
          newLoginRedirect,
          0,
          '/',
          undefined,
          this.$fischer.isSecureConnection(),
          'None',
        );
        window.location.href = newLoginRedirect;
        return true;
      } else {
        this.logDebug('not a custom login page', brand);
        if (brand) {
          this.logDebug('removing login-redirect');
          this.$cookies.remove(loginRedirectCookie);
        }
      }

      if (!(await this.setCookies(cookies))) {
        return true;
      }

      if (redirectUri && state) {
        if (username && password) {
          this.logDebug('attempting to submit login in a second');
          this.form.username = username;
          this.form.password = password;
          this.apiData = {};
          this.apiDataLoading = false;

          setTimeout(() => {
            this.logDebug('attempting to submit login in a second');
            if (this.$refs.loginForm) {
              this.submit();
            } else {
              console.error('no refs.loginForm');
            }
          }, 1000);

          return true;
        }

        this.initializeAuthorization(redirectUri, state, nonce, scope, responseType);
        return true;
      }

      this.logDebug('Did not get login request from hash');
      return false;
    },
    async loadApiData(lang, apiKeyParam) {
      try {
        this.apiDataLoading = true;

        const langEncoded = encodeURIComponent(lang);

        const apiKey = (
          apiKeyParam ||
          this.fields.defaultApiKey?.value ||
          (IS_SHOWROOM && (this.$fischer.query('api_key') || 'fiwe')) ||
          ''
        ).replace(/[^A-Z0-9a-z-]/g, '');

        let url = '/sso/api/app';
        if (IS_SHOWROOM) {
          url = `/__mocks__/sitecoreApi${url}/mock_${langEncoded}_${apiKey}.json`;
        } else {
          url = `${url}?lang=${langEncoded}&apiKey=${apiKey}`;
        }
        this.logDebug('loading api data with apiKey', apiKey);
        const { data } = await axios.get(url);
        if (!data.Submit) {
          if (lang !== 'en') {
            console.info(`Could not load data for '${lang}' in '${apiKeyParam}', fallback to en`);
            await this.loadApiData('en', apiKeyParam);
          } else {
            throw new Error('Could not load en api data');
          }
        } else {
          this.apiData = data;
          this.logDebug('got api data');
        }

        const languages = this.apiData?.Available_Languages || [];
        if (languages.some((pageLang) => pageLang.Value === lang)) {
          this.interfaceLanguage = lang;
        }
      } catch (error) {
        this.criticalLoginFailure('Failed to load api', error);
      } finally {
        this.apiDataLoading = false;
      }
    },
    initializeAuthorization(redirectUri, state, nonce, scope, responseType) {
      return goToLoginFunction(
        this.logDebugWrapper,
        this.clientId,
        this.authorizeRedirect,
        redirectUri,
        state,
        nonce,
        scope,
        responseType,
      );
    },
    handleLoginFormPage() {
      this.logDebug('handling login form page');

      const cookie = this.getCookies();

      const {
        brand = 'fiwe',
        clientId,
        apiKey,
        lang,
        username: cookieUsername,
      } = {
        ...cookie,
        ...this.referrerData,
      };
      this.brand = brand;
      this.lang = lang || this.getBrowserLang();

      if (clientId) {
        this.clientId = clientId;
      } else {
        this.clientId = `${this.brand}-${this.lang.replace(/[_-].*/, '').toLowerCase()}`;
      }

      this.logDebug(`determined brand="${brand}" apiKey="${apiKey}" clientId="${this.clientId}"`);

      const username = this.$fischer.query('username') || cookieUsername;
      if (username) {
        this.form.username = username;
      }

      if (
        brand !== cookie.brand ||
        this.clientId !== cookie.clientId ||
        apiKey !== cookie.apiKey ||
        username !== cookieUsername
      ) {
        this.logDebug('updating cookie');
        this.setCookies({
          ...cookie,
          brand,
          clientId: this.clientId,
          apiKey,
          username,
          ...(lang ? { lang } : {}),
        });
      }

      if (this.$fischer.query('authentication_error') === 'true') {
        const loginRedirect = this.$cookies.get(loginRedirectCookie);
        this.logDebug('authentication error', loginRedirect);
        if (loginRedirect && loginRedirect !== 'null') {
          this.logDebug('authentication_error, redirecting');
          window.location.href = loginRedirect.replace('?', '?authentication_error=true&');
          return true;
        }
        this.isLoginFailure = true;
      }

      // do not await, let spinner show
      this.loadApiData(this.lang, apiKey);

      this.logDebug('handled login form page successfully');
      return true;
    },
    onChangeUsername(value) {
      this.form.username = value;
      this.usernameChangedOrBlurred = true;
    },
    onChangePassword(value) {
      this.passwordChangedOrBlurred = true;
      this.form.password = value;
    },
    changeLanguage({ value: lang }) {
      if (typeof lang !== 'string') {
        return;
      }
      this.interfaceLanguage = lang;

      const { apiKey } = this.getCookies();
      this.loadApiData(lang, apiKey);
    },
    getBrowserLang() {
      const fallback = 'en';

      const languages = this.apiData?.Available_Languages;
      if (!languages) {
        return fallback;
      }
      const browserLang = window.navigator.language;
      if (!browserLang) {
        return fallback;
      }

      const directMatch = languages.find((lang) => lang.Value === browserLang);
      if (directMatch) {
        return directMatch.Value;
      }

      const shortLang = browserLang.replace(/-.*/, '');
      const shortMatch = languages.find((lang) => lang.Value.startsWith(shortLang));
      if (shortMatch) {
        return shortMatch.Value;
      }

      return fallback;
    },
    onEnterEmail() {
      if (!this.form.username) {
        return;
      }
      this.$refs.password.focus();
    },
    onEnterPassword() {
      if (!this.form.username || !this.form.password || !this.formValid) {
        this.logDebug(
          'cancelling password-enter',
          Boolean(this.form.username),
          Boolean(this.form.password),
          this.formValid,
        );
        return;
      }
      this.submit();
    },
    submit() {
      if (this.isSubmitting) {
        console.error('already submitting');
        return;
      }
      this.isSubmitting = true;
      const cookie = this.getCookies();
      if (cookie.username !== this.form.username) {
        this.setCookies({
          ...cookie,
          username: this.form.username,
        });
      }
      // wait for input to be mirrored in the cloned form
      this.$nextTick(() => {
        this.$refs.loginForm.submit();
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import './login-form-page-fi';
</style>
